import React, { useEffect, useRef, useState } from "react";

const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);
const ICONS = {
  check: "M20 6L9 17l-5-5",
  x:     "M18 6L6 18M6 6l12 12",
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');

  .vp-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: vp-shimmer 8s linear infinite;
  }
  @keyframes vp-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .jdc-divider {
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(249,115,22,0.4) 20%, rgba(168,85,247,0.5) 50%, rgba(6,182,212,0.4) 80%, transparent);
  }
  @keyframes jdc-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .jdc-fade   { animation: jdc-fadeUp 0.6s ease forwards; }
  .jdc-fade-1 { animation-delay: 0.1s; opacity: 0; }
  .jdc-fade-2 { animation-delay: 0.2s; opacity: 0; }
  .jdc-fade-3 { animation-delay: 0.3s; opacity: 0; }
  .jdc-fade-4 { animation-delay: 0.4s; opacity: 0; }
  .jdc-fade-5 { animation-delay: 0.5s; opacity: 0; }

  @keyframes jdc-badge-pop {
    0%   { transform: scale(0.8); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  .jdc-badge { transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
  .jdc-badge:hover { transform: translateY(-4px) scale(1.05); }
  .jdc-badge-anim { animation: jdc-badge-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) backwards; }

  .jdc-feature-card { transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
  .jdc-feature-card:hover { transform: translateY(-10px) scale(1.03); }

  .jdc-bento-card { transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
  .jdc-bento-card:hover { transform: translateY(-6px); }

  /* ── Atlas orbit / pulse animations ── */
  @keyframes atl-orbit {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .atl-orbit-cw  { animation: atl-orbit 12s linear infinite; transform-origin: 40px 40px; }
  .atl-orbit-ccw { animation: atl-orbit 18s linear infinite reverse; transform-origin: 40px 40px; }

  @keyframes atl-core-pulse {
    0%, 100% { opacity: 0.7; r: 10; }
    50%       { opacity: 1;   r: 13; }
  }
  .atl-core { animation: atl-core-pulse 3s ease-in-out infinite; }

  @keyframes atl-node-blink {
    0%, 100% { opacity: 0.3; }
    50%       { opacity: 1;   }
  }
  .atl-node { animation: atl-node-blink 2s ease-in-out infinite; }

  @keyframes atl-beam-draw {
    from { stroke-dashoffset: 120; opacity: 0.1; }
    to   { stroke-dashoffset: 0;   opacity: 0.8; }
  }
  .atl-beam { stroke-dasharray: 120; animation: atl-beam-draw 2s ease-in-out infinite alternate; }

  @keyframes atl-ring-expand {
    0%   { r: 24; opacity: 0.5; }
    100% { r: 36; opacity: 0; }
  }
  .atl-ring-expand { animation: atl-ring-expand 2.5s ease-out infinite; }

  /* ── Bridge section animations ── */
  @keyframes atl-flow-right {
    from { stroke-dashoffset: 60; }
    to   { stroke-dashoffset: 0; }
  }
  @keyframes atl-flow-left {
    from { stroke-dashoffset: 60; }
    to   { stroke-dashoffset: 0; }
  }
  .atl-flow-r { stroke-dasharray: 60; animation: atl-flow-right 1.8s linear infinite; }
  .atl-flow-l { stroke-dasharray: 60; animation: atl-flow-left  1.8s linear infinite reverse; }

  @keyframes atl-signal-travel {
    0%   { opacity: 0; transform: translateX(0); }
    30%  { opacity: 1; }
    70%  { opacity: 1; }
    100% { opacity: 0; transform: translateX(60px); }
  }
  @keyframes atl-signal-return {
    0%   { opacity: 0; transform: translateX(0); }
    30%  { opacity: 1; }
    70%  { opacity: 1; }
    100% { opacity: 0; transform: translateX(-60px); }
  }
  .atl-sig-fwd { animation: atl-signal-travel 2.2s ease-in-out infinite; }
  .atl-sig-rev { animation: atl-signal-return  2.2s ease-in-out infinite 1.1s; }

  /* ── Process flow ── */
  @keyframes atl-merge-pulse {
    0%, 100% { opacity: 0.4; }
    50%       { opacity: 1;   }
  }
  .atl-merge { animation: atl-merge-pulse 2s ease-in-out infinite; }

  @keyframes atl-match-ring {
    from { stroke-dashoffset: 100; }
    to   { stroke-dashoffset: 18; }
  }
  .atl-match-arc { stroke-dasharray: 100; animation: atl-match-ring 2.2s cubic-bezier(0.22,1,0.36,1) infinite alternate; }

  @keyframes atl-data-drop {
    0%   { transform: translateY(-4px); opacity: 0; }
    100% { transform: translateY(0);    opacity: 1; }
  }
  .atl-d1 { animation: atl-data-drop 0.5s ease 0.0s infinite alternate; }
  .atl-d2 { animation: atl-data-drop 0.5s ease 0.18s infinite alternate; }
  .atl-d3 { animation: atl-data-drop 0.5s ease 0.36s infinite alternate; }

  @keyframes atl-place-check {
    from { stroke-dashoffset: 50; opacity: 0; }
    to   { stroke-dashoffset: 0;  opacity: 1; }
  }
  .atl-check { stroke-dasharray: 50; animation: atl-place-check 1.2s ease-out infinite alternate; }

  @keyframes atl-loop-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .atl-loop-arrow { animation: atl-loop-spin 4s linear infinite; transform-origin: 34px 28px; }

  .atl-process-card { transition: all 0.3s ease; }
  .atl-process-card:hover { transform: translateY(-6px); }

  /* ── Bento accent animations ── */
  @keyframes atl-scan-anim {
    0%   { transform: translateY(0); opacity: 1; }
    90%  { opacity: 0.6; }
    100% { transform: translateY(44px); opacity: 0; }
  }
  .atl-scan { animation: atl-scan-anim 2.2s ease-in-out infinite; }

  @keyframes atl-grid-draw {
    from { stroke-dashoffset: 160; opacity: 0; }
    to   { stroke-dashoffset: 0;   opacity: 1; }
  }
  .atl-grid-line { stroke-dasharray: 160; animation: atl-grid-draw 2s ease-out infinite alternate; }

  @keyframes atl-pipe-pulse {
    0%, 100% { opacity: 0.35; }
    50%       { opacity: 1;    }
  }
  .atl-pipe { animation: atl-pipe-pulse 1.8s ease-in-out infinite; }

  /* ── Comparison ── */
  .jdc-comparison-card { position: relative; overflow: hidden; transition: all 0.3s ease; }
  .jdc-comparison-card::before {
    content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent);
    transition: left 0.5s ease;
  }
  .jdc-comparison-card:hover::before { left: 100%; }
  .jdc-comparison-card:hover { transform: translateY(-4px); }
  .jdc-comparison-item { transition: all 0.2s ease; }
  .jdc-comparison-item:hover { transform: translateX(4px); }
  @keyframes jdc-check-pop {
    0%   { transform: scale(0); opacity: 0; }
    50%  { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
  }
  .jdc-check-icon { animation: jdc-check-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards; }
  .jdc-x-icon:hover { animation: jdc-x-shake 0.4s ease-in-out; }
  @keyframes jdc-x-shake {
    0%, 100% { transform: rotate(0deg); }
    25%       { transform: rotate(-3deg); }
    75%       { transform: rotate(3deg); }
  }

  /* ── Timeline ── */
  @keyframes atl-tl-line-grow {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  .atl-h-timeline-line { animation: atl-tl-line-grow 2.4s cubic-bezier(0.22,1,0.36,1) 0.4s both; transform-origin: left; }

  @keyframes atl-glow-pulse {
    0%, 100% { opacity: 0.25; }
    50%       { opacity: 0.65; }
  }
  .atl-glow { animation: atl-glow-pulse 3s ease-in-out infinite; }

  .atl-tl-card { transition: all 0.3s ease; }
  .atl-tl-card:hover { transform: translateY(-8px); }

  @keyframes jdc-timeline-draw {
    from { height: 0%; }
    to   { height: 100%; }
  }
  .jdc-timeline-line { animation: jdc-timeline-draw 1.5s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both; }
  .jdc-timeline-card { transition: all 0.3s ease; }
  .jdc-timeline-card:hover { transform: translateX(6px); }
`;

// ─── DATA ─────────────────────────────────────────────────────────────────────

const BADGES = [
  "Bidirectional Match Engine", "Role-Candidate Signal Sync",
  "Closed-Loop Placement Intelligence", "Cross-Platform Governance",
  "Enterprise Compliance Hub", "Full-Funnel Analytics",
  "Workflow Orchestration", "Real-Time Gap Alignment",
];

const FEATURES = [
  { icon: "🔗", title: "Unified Intelligence Layer", text: "Connects role blueprints from Codex with candidate profiles from Odyssey into a single, bidirectional matching engine — where both sides inform and sharpen each other continuously.", color: "orange" },
  { icon: "🌐", title: "Cross-Platform Orchestration", text: "Automate end-to-end hiring workflows that span both sides of the equation — from role requisition through offer acceptance — with zero manual handoffs between systems.", color: "violet" },
  { icon: "📡", title: "Real-Time Signal Exchange", text: "Sync role requirement signals and candidate positioning signals in real time — so matches sharpen as both sides evolve and every decision is based on live intelligence.", color: "cyan" },
  { icon: "🏛️", title: "Enterprise Governance Hub", text: "Centralize compliance, audit trails, security clearances, and structured approval workflows across all teams, entities, and geographies — from a single control layer.", color: "emerald" },
  { icon: "📈", title: "Ecosystem Analytics", text: "Full-funnel intelligence from role definition quality to candidate conversion rate — surfacing bottlenecks on both sides and benchmarking performance across every hiring cycle.", color: "amber" },
  { icon: "🤝", title: "Closed-Loop Placement Intelligence", text: "Every placement feeds structured outcome data back into both Codex and Odyssey — continuously retraining role blueprints and candidate positioning for every future hire.", color: "pink" },
];

const FLOW_STEPS = [
  { label: "INGEST",      color: "#fb923c", description: "Codex defines the role blueprint. Odyssey positions the candidate. Atlas ingests both sides of the equation simultaneously." },
  { label: "ALIGN",       color: "#a78bfa", description: "Atlas runs bidirectional gap analysis — aligning role requirements to candidate signals and surfacing precision match scores in real time." },
  { label: "ORCHESTRATE", color: "#22d3ee", description: "Automated workflows coordinate screening, structured interviews, and evaluation rubrics across all stakeholders — with zero manual handoffs." },
  { label: "CLOSE",       color: "#34d399", description: "Placement intelligence closes the loop — capturing outcome data that refines both role blueprints and candidate positioning for the next cycle." },
];

const COMPARISON = {
  title: "Fragmented Hiring Ecosystem vs. Unified Intelligence Platform",
  beforeTitle: "Without Atlas",
  afterTitle:  "With HEKA Atlas",
  before: [
    "Role teams and candidate teams operating in separate, disconnected tools",
    "Match quality dependent on human judgment with no shared data layer",
    "Workflow handoffs lost between sourcing, screening, and evaluation",
    "No visibility into why placements succeed or fail — every hire starts from zero",
    "Compliance and governance enforced manually, inconsistently, at scale",
  ],
  after: [
    "Role intelligence and career intelligence unified in a single orchestrated system",
    "Bidirectional match scoring powered by live signals from both sides",
    "End-to-end workflows automated from requisition to offer acceptance",
    "Every placement feeds outcome intelligence back into the next hire cycle",
    "Centralized governance, audit trails, and compliance enforced automatically",
  ],
};

const TIMELINE_STEPS = [
  { step: "01", title: "Define the Role",          subtitle: "Codex · Role Blueprint Engine",                         text: "Role requirements, competency frameworks, and seniority signals are structured by Codex into a precision role blueprint — the foundation Atlas uses for every downstream decision.",               color: "#f97316", nodeColor: "#f97316", glowColor: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.25)" },
  { step: "02", title: "Position the Candidate",   subtitle: "Odyssey · Resume Intelligence · Career Path Mapper",   text: "Odyssey optimizes the candidate's profile, scores their fit against the role blueprint, and surfaces a structured readiness signal — before a single interview is scheduled.",                  color: "#a855f7", nodeColor: "#a855f7", glowColor: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.25)" },
  { step: "03", title: "Match & Align",             subtitle: "Atlas · Bidirectional Match Engine",                   text: "Atlas ingests both sides simultaneously — scoring role-to-candidate alignment across skills, depth, and fit. Gaps are surfaced and ranked before a human reviews a single profile.",          color: "#22d3ee", nodeColor: "#22d3ee", glowColor: "rgba(34,211,238,0.12)",  border: "rgba(34,211,238,0.25)" },
  { step: "04", title: "Evaluate & Interview",      subtitle: "Atlas · Interview Intelligence · Bias Engine",         text: "Structured interview frameworks, competency-mapped question sets, and bias detection run automatically — ensuring every evaluator operates from the same objective rubric.",                   color: "#10b981", nodeColor: "#10b981", glowColor: "rgba(16,185,129,0.12)",  border: "rgba(16,185,129,0.25)" },
  { step: "05", title: "Place with Confidence",     subtitle: "Atlas · Offer Intelligence · Workflow Automation",     text: "Ranked shortlists, decision-ready reports, and structured offer workflows are generated automatically — so every hiring decision is data-backed and every approval is audit-ready.",          color: "#f59e0b", nodeColor: "#f59e0b", glowColor: "rgba(245,158,11,0.12)",   border: "rgba(245,158,11,0.25)" },
  { step: "06", title: "Close the Loop",            subtitle: "Atlas · Placement Intelligence",                       text: "Outcome data from every placement is fed back into Codex role blueprints and Odyssey candidate profiles — closing the intelligence loop and improving every future hire automatically.",   color: "#ec4899", nodeColor: "#ec4899", glowColor: "rgba(236,72,153,0.12)",  border: "rgba(236,72,153,0.25)" },
];

// ─── BENTO ACCENT SVGs ────────────────────────────────────────────────────────

// Enterprise RPO — two orbiting systems merging into one core
const AccentRPO = () => (
  <svg width="80" height="60" viewBox="0 0 80 60">
    <circle cx="40" cy="30" r="24" fill="none" stroke="rgba(251,146,60,0.08)" strokeWidth="1" />
    <circle cx="40" cy="30" r="16" fill="none" stroke="rgba(251,146,60,0.12)" strokeWidth="1" />
    {/* Outer orbit with two satellites */}
    <g className="atl-orbit-cw">
      <circle cx="40" cy="6"  r="3.5" fill="rgba(251,146,60,0.8)" />
      <circle cx="40" cy="54" r="2.5" fill="rgba(251,146,60,0.5)" />
    </g>
    {/* Inner orbit */}
    <g className="atl-orbit-ccw">
      <circle cx="56" cy="30" r="2.5" fill="rgba(251,146,60,0.65)" />
      <circle cx="24" cy="30" r="2"   fill="rgba(251,146,60,0.4)" />
    </g>
    {/* Core */}
    <circle cx="40" cy="30" r="8" fill="rgba(251,146,60,0.12)" stroke="rgba(251,146,60,0.5)" strokeWidth="1.5" className="atl-core" />
    <circle cx="40" cy="30" r="3.5" fill="rgba(251,146,60,0.9)" />
  </svg>
);

// Enterprise Organizations — grid + beam signals
const AccentEnterprise = () => (
  <svg width="80" height="60" viewBox="0 0 80 60">
    {[0,1,2].map(i=>(
      <line key={`h${i}`} x1="8" y1={14+i*13} x2="56" y2={14+i*13}
        stroke="rgba(168,85,247,0.35)" strokeWidth="1.2" className="atl-grid-line"
        style={{animationDelay:`${i*0.2}s`}} />
    ))}
    {[0,1,2,3].map(i=>(
      <line key={`v${i}`} x1={8+i*16} y1="14" x2={8+i*16} y2="40"
        stroke="rgba(168,85,247,0.18)" strokeWidth="1" className="atl-grid-line"
        style={{animationDelay:`${0.1+i*0.15}s`}} />
    ))}
    <rect x="60" y="10" width="16" height="12" rx="2" fill="rgba(168,85,247,0.1)" stroke="rgba(168,85,247,0.45)" strokeWidth="1" />
    <text x="68" y="20" textAnchor="middle" fill="rgba(168,85,247,0.85)" fontSize="6.5" fontFamily="monospace" fontWeight="bold">ATL</text>
    <line x1="56" y1="16" x2="60" y2="16" stroke="rgba(168,85,247,0.5)" strokeWidth="1" className="atl-beam" />
    <circle cx="4" cy="27" r="3" fill="rgba(168,85,247,0.6)" className="atl-node" />
  </svg>
);

// Staffing Platforms — pipeline nodes
const AccentStaffing = () => (
  <svg width="80" height="60" viewBox="0 0 80 60">
    {[0,1,2,3].map(i=>(
      <g key={i}>
        <circle cx={12+i*18} cy="30" r="7"
          fill="rgba(34,211,238,0.1)" stroke="rgba(34,211,238,0.55)" strokeWidth="1.5"
          className="atl-pipe" style={{animationDelay:`${i*0.25}s`}} />
        {i < 3 && <line x1={19+i*18} y1="30" x2={23+i*18} y2="30"
          stroke="rgba(34,211,238,0.4)" strokeWidth="1.5" />}
      </g>
    ))}
    {["DEF","MTH","EVL","PLC"].map((lbl,i)=>(
      <text key={i} x={12+i*18} y="46" textAnchor="middle"
        fill="rgba(34,211,238,0.55)" fontSize="6" fontFamily="monospace">{lbl}</text>
    ))}
    <circle cx="66" cy="30" r="4" fill="rgba(34,211,238,0.9)" />
    <path d="M63,28 L67,30 L63,32" fill="rgba(34,211,238,0.9)" />
  </svg>
);

// Platform Integrators — match score ring with dual signal
const AccentIntegrators = () => (
  <svg width="80" height="60" viewBox="0 0 80 60">
    <circle cx="40" cy="30" r="22" fill="none" stroke="rgba(16,185,129,0.1)" strokeWidth="7" />
    <circle cx="40" cy="30" r="22" fill="none" stroke="rgba(16,185,129,0.65)" strokeWidth="7"
      strokeLinecap="round" className="atl-match-arc"
      style={{transform:"rotate(-90deg)", transformOrigin:"40px 30px"}} />
    <text x="40" y="27" textAnchor="middle" fill="rgba(16,185,129,0.9)" fontSize="11" fontFamily="monospace" fontWeight="bold">98%</text>
    <text x="40" y="38" textAnchor="middle" fill="rgba(16,185,129,0.5)" fontSize="6.5" fontFamily="monospace">UNIFIED</text>
  </svg>
);

// ─── PROCESS FLOW ACCENT SVGs ─────────────────────────────────────────────────

// INGEST — two document streams merging
const PfIngest = () => (
  <svg width="68" height="56" viewBox="0 0 68 56">
    {/* Left doc (Codex) */}
    <rect x="2" y="6" width="24" height="32" rx="2.5" fill="rgba(251,146,60,0.07)" stroke="rgba(251,146,60,0.4)" strokeWidth="1.5" className="atl-d1" />
    {[0,1,2].map(i=>(
      <rect key={i} x="6" y={12+i*8} width={[14,10,16][i]} height="4" rx="1.5" fill="rgba(251,146,60,0.3)" />
    ))}
    {/* Right doc (Odyssey) */}
    <rect x="42" y="6" width="24" height="32" rx="2.5" fill="rgba(168,85,247,0.07)" stroke="rgba(168,85,247,0.4)" strokeWidth="1.5" className="atl-d2" />
    {[0,1,2].map(i=>(
      <rect key={i} x="46" y={12+i*8} width={[16,10,14][i]} height="4" rx="1.5" fill="rgba(168,85,247,0.3)" />
    ))}
    {/* Merge arrows */}
    <path d="M26,22 L32,28 L26,34" fill="none" stroke="rgba(251,146,60,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M42,22 L36,28 L42,34" fill="none" stroke="rgba(168,85,247,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Atlas core node */}
    <circle cx="34" cy="28" r="5" fill="rgba(34,211,238,0.15)" stroke="rgba(34,211,238,0.65)" strokeWidth="1.5" className="atl-merge" />
    <text x="34" y="49" textAnchor="middle" fill="rgba(34,211,238,0.65)" fontSize="6.5" fontFamily="monospace">INGEST</text>
  </svg>
);

// ALIGN — bidirectional match scoring rings
const PfAlign = () => (
  <svg width="68" height="56" viewBox="0 0 68 56">
    <circle cx="34" cy="26" r="20" fill="none" stroke="rgba(167,139,250,0.1)" strokeWidth="6" />
    <circle cx="34" cy="26" r="20" fill="none" stroke="rgba(167,139,250,0.65)" strokeWidth="6"
      strokeLinecap="round" className="atl-match-arc"
      style={{transform:"rotate(-90deg)", transformOrigin:"34px 26px"}} />
    <text x="34" y="23" textAnchor="middle" fill="rgba(167,139,250,0.9)" fontSize="11" fontFamily="monospace" fontWeight="bold">94</text>
    <text x="34" y="33" textAnchor="middle" fill="rgba(167,139,250,0.5)" fontSize="6" fontFamily="monospace">ALIGN %</text>
    {/* Side signal dots */}
    <circle cx="4"  cy="26" r="3" fill="rgba(251,146,60,0.7)"  className="atl-node" />
    <circle cx="64" cy="26" r="3" fill="rgba(34,211,238,0.7)" className="atl-node" style={{animationDelay:"0.5s"}} />
    <line x1="7" y1="26" x2="14" y2="26" stroke="rgba(251,146,60,0.4)"  strokeWidth="1" strokeDasharray="2,2" />
    <line x1="54" y1="26" x2="61" y2="26" stroke="rgba(34,211,238,0.4)" strokeWidth="1" strokeDasharray="2,2" />
  </svg>
);

// ORCHESTRATE — three workflow blocks dropping in sequence
const PfOrchestrate = () => (
  <svg width="68" height="56" viewBox="0 0 68 56">
    <rect x="6"  y="4"  width="56" height="12" rx="2.5" fill="rgba(34,211,238,0.15)" stroke="rgba(34,211,238,0.5)" strokeWidth="1.5" className="atl-d1" />
    <rect x="6"  y="21" width="44" height="11" rx="2.5" fill="rgba(34,211,238,0.1)"  stroke="rgba(34,211,238,0.4)" strokeWidth="1.5" className="atl-d2" />
    <rect x="6"  y="37" width="34" height="11" rx="2.5" fill="rgba(34,211,238,0.06)" stroke="rgba(34,211,238,0.3)" strokeWidth="1.5" className="atl-d3" />
    <text x="10" y="14" fill="rgba(34,211,238,0.8)"  fontSize="6.5" fontFamily="monospace">SCREEN → INTERVIEW</text>
    <text x="10" y="30" fill="rgba(34,211,238,0.65)" fontSize="6.5" fontFamily="monospace">EVALUATE → SCORE</text>
    <text x="10" y="46" fill="rgba(34,211,238,0.5)"  fontSize="6.5" fontFamily="monospace">APPROVE → OFFER</text>
  </svg>
);

// CLOSE — circular loop arrow with placement checkmark
const PfClose = () => (
  <svg width="68" height="56" viewBox="0 0 68 56">
    {/* Loop ring */}
    <circle cx="34" cy="28" r="20" fill="none" stroke="rgba(52,211,153,0.15)" strokeWidth="2" strokeDasharray="4,3" className="atl-loop-arrow" />
    {/* Arrow tip on the ring */}
    <path d="M54,20 L58,28 L50,28" fill="none" stroke="rgba(52,211,153,0.65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Center check */}
    <circle cx="34" cy="28" r="12" fill="rgba(52,211,153,0.08)" stroke="rgba(52,211,153,0.4)" strokeWidth="1.5" />
    <path d="M27,28 L32,34 L42,21" fill="none" stroke="rgba(52,211,153,0.9)" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" className="atl-check" />
    <text x="34" y="50" textAnchor="middle" fill="rgba(52,211,153,0.55)" fontSize="6.5" fontFamily="monospace">LOOP</text>
  </svg>
);

// ─── INTEGRATION BRIDGE ───────────────────────────────────────────────────────
// The unique Atlas section — visualises Codex + Odyssey feeding into Atlas

const IntegrationBridge = ({ setCurrentPage }) => (
  <section className="jdc-fade jdc-fade-2">
    <div className="text-center mb-14">
      <p className="text-xs font-bold tracking-widest uppercase text-cyan-400 mb-3">Platform Architecture</p>
      <h2 className="text-3xl font-bold text-slate-100 tracking-tight">One System. Both Sides of the Hire.</h2>
      <p className="text-slate-400 mt-4 max-w-xl mx-auto text-sm leading-relaxed">
        Atlas is not a third product — it is the layer that makes Codex and Odyssey work as a single, closed-loop intelligence system.
      </p>
    </div>

    {/* Bridge visual */}
    <div className="relative max-w-5xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 items-stretch">

        {/* Codex node */}
        <div className="rounded-2xl border border-orange-800/40 bg-slate-900 p-7 flex flex-col gap-4 relative"
          style={{background:"radial-gradient(ellipse at 20% 50%, rgba(251,146,60,0.08) 0%, transparent 60%), #0f172a"}}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-orange-400 text-xs font-bold tracking-widest uppercase">Heka Codex</span>
          </div>
          <p className="text-slate-100 font-bold text-lg leading-snug">Role Intelligence</p>
          <p className="text-slate-400 text-sm leading-relaxed flex-1">
            Blueprints, JDs, skill taxonomies, candidate match scores, interview rubrics, and bias signals — all structured and ready for Atlas to consume.
          </p>
          <div className="space-y-2 pt-2 border-t border-orange-800/30">
            {["Role Blueprints","JD & Resume Parsing","Match Scoring","Interview Frameworks"].map((item,i)=>(
              <div key={i} className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-orange-400 flex-shrink-0" />
                <span className="text-orange-300/70 text-xs">{item}</span>
              </div>
            ))}
          </div>
          {/* Explore button */}
          <div className="flex justify-end pt-3">
            <button
              onClick={() => setCurrentPage('HekaCodex')}
              className="px-4 py-2 rounded-lg bg-orange-600/20 border border-orange-600/40 text-orange-400 text-xs font-semibold tracking-wide uppercase hover:bg-orange-600/30 hover:border-orange-500/60 hover:text-orange-300 transition-all duration-300 hover:scale-105"
            >
              Explore Codex
            </button>
          </div>
        </div>

        {/* Atlas center connector */}
        <div className="flex flex-col items-center justify-center py-8 md:py-0 relative">
          {/* Vertical/horizontal lines on mobile/desktop */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px"
            style={{background:"linear-gradient(90deg, rgba(251,146,60,0.4), rgba(34,211,238,0.5), rgba(168,85,247,0.4))"}}>
          </div>
          {/* Atlas core badge */}
          <div className="relative z-10 rounded-2xl border border-cyan-700/50 p-6 flex flex-col items-center gap-3 backdrop-blur-sm"
            style={{background:"radial-gradient(ellipse at 50% 50%, rgba(34,211,238,0.1) 0%, rgba(15,23,42,0.95) 70%), #0f172a",
                    boxShadow:"0 0 40px rgba(34,211,238,0.08), 0 0 80px rgba(34,211,238,0.04)"}}>
            {/* Animated core SVG */}
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(34,211,238,0.08)" strokeWidth="1" />
              <circle cx="40" cy="40" r="22" fill="none" stroke="rgba(34,211,238,0.12)" strokeWidth="1" />
              <circle cx="40" cy="40" className="atl-ring-expand" fill="none" stroke="rgba(34,211,238,0.3)" strokeWidth="1" />
              <g className="atl-orbit-cw">
                <circle cx="40" cy="8" r="3" fill="rgba(251,146,60,0.8)" />
              </g>
              <g className="atl-orbit-ccw">
                <circle cx="72" cy="40" r="2.5" fill="rgba(168,85,247,0.8)" />
              </g>
              <circle cx="40" cy="40" r="14" fill="rgba(34,211,238,0.06)" stroke="rgba(34,211,238,0.4)" strokeWidth="1.5" />
              <circle cx="40" cy="40" r="6" fill="rgba(34,211,238,0.9)" />
            </svg>
            <div className="text-cyan-400 font-bold text-sm tracking-widest uppercase">HEKA ATLAS</div>
            <div className="text-slate-500 text-xs text-center leading-relaxed max-w-28">Unified Orchestration Layer</div>
          </div>
        </div>

        {/* Odyssey node */}
        <div className="rounded-2xl border border-violet-800/40 bg-slate-900 p-7 flex flex-col gap-4 relative"
          style={{background:"radial-gradient(ellipse at 80% 50%, rgba(168,85,247,0.08) 0%, transparent 60%), #0f172a"}}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
            <span className="text-violet-400 text-xs font-bold tracking-widest uppercase">Heka Odyssey</span>
          </div>
          <p className="text-slate-100 font-bold text-lg leading-snug">Career Intelligence</p>
          <p className="text-slate-400 text-sm leading-relaxed flex-1">
            Optimized profiles, job fit scores, interview readiness signals, and offer intelligence — all structured and ready for Atlas to match against every role.
          </p>
          <div className="space-y-2 pt-2 border-t border-violet-800/30">
            {["Candidate Positioning","Job Fit Scoring","Interview Readiness","Offer Intelligence"].map((item,i)=>(
              <div key={i} className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-violet-400 flex-shrink-0" />
                <span className="text-violet-300/70 text-xs">{item}</span>
              </div>
            ))}
          </div>
          {/* Explore button */}
          <div className="flex justify-end pt-3">
            <button
              onClick={() => setCurrentPage('HekaOdyssey')}
              className="px-4 py-2 rounded-lg bg-violet-600/20 border border-violet-600/40 text-violet-400 text-xs font-semibold tracking-wide uppercase hover:bg-violet-600/30 hover:border-violet-500/60 hover:text-violet-300 transition-all duration-300 hover:scale-105"
            >
              Explore Odyssey
            </button>
          </div>
        </div>

      </div>

      {/* Bottom output bar */}
      <div className="mt-6 rounded-2xl border border-cyan-800/30 p-5 flex flex-col md:flex-row items-center gap-4 md:gap-8 justify-center"
        style={{background:"radial-gradient(ellipse at 50% 100%, rgba(34,211,238,0.05) 0%, transparent 60%), #0f172a"}}>
        <span className="text-xs font-bold tracking-widest uppercase text-cyan-400/60">Atlas Output</span>
        <div className="w-px h-6 bg-cyan-800/40 hidden md:block" />
        {["Bidirectional Match Intelligence","Closed-Loop Placement Outcomes","Unified Governance & Compliance","Full-Funnel Ecosystem Analytics"].map((cap,i)=>(
          <div key={i} className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
            <span className="text-slate-300 text-xs font-medium">{cap}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

const FeatureGrid = () => (
  <section className="jdc-fade jdc-fade-3">
    <div className="text-center mb-14">
      <p className="text-xs font-bold tracking-widest uppercase text-orange-400 mb-3">Platform Capabilities</p>
      <h2 className="text-3xl font-bold text-slate-100 tracking-tight">The Full Stack of Unified Hiring Intelligence</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {FEATURES.map((f, i) => (
        <div key={i} className={`jdc-feature-card rounded-2xl border border-${f.color}-800/30 bg-gradient-to-br from-${f.color}-900/10 to-slate-900/50 p-7 hover:border-${f.color}-700/50 backdrop-blur-sm group`}>
          <div className="text-5xl mb-5">{f.icon}</div>
          <h3 className={`text-${f.color}-400 font-bold text-lg mb-3 tracking-tight group-hover:text-${f.color}-300 transition-colors`}>{f.title}</h3>
          <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-300 transition-colors">{f.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const BadgePills = () => (
  <section className="jdc-fade jdc-fade-3">
    <div className="text-center mb-12">
      <p className="text-xs font-bold tracking-widest uppercase text-violet-400 mb-3">System Capabilities</p>
      <h2 className="text-3xl font-bold text-slate-100 tracking-tight">Every Dimension of the Hire, Unified</h2>
    </div>
    <div className="flex flex-wrap gap-3 justify-center max-w-4xl mx-auto">
      {BADGES.map((b, i) => (
        <div key={i} className="jdc-badge jdc-badge-anim px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-900/30 to-teal-900/30 border border-cyan-700/40 text-cyan-300 text-sm font-semibold backdrop-blur-sm hover:border-cyan-500/60 hover:text-cyan-200 cursor-default"
          style={{ animationDelay: `${i * 0.05}s` }}>{b}</div>
      ))}
    </div>
  </section>
);

const BentoCards = () => (
  <section className="jdc-fade jdc-fade-3">
    <div className="text-center mb-12">
      <p className="text-xs font-bold tracking-widest uppercase text-emerald-400 mb-3">Built For Every Hiring Operation</p>
      <h2 className="text-3xl font-bold text-slate-100 tracking-tight">Who Atlas Is Designed For</h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

      {/* RPO & Managed Service Providers — tall left */}
      <div className="jdc-bento-card md:row-span-2 rounded-2xl border border-amber-800/40 hover:border-amber-500/50 bg-slate-900 p-8 flex flex-col"
        style={{ background: "radial-gradient(ellipse at 30% 80%, rgba(251,146,60,0.07) 0%, transparent 60%), #0f172a" }}>
        <div className="flex justify-center py-2 mb-6">
          <AccentRPO />
        </div>
        <h3 className="text-amber-400 font-bold text-xl tracking-tight mb-3">RPO & Managed Service Providers</h3>
        <p className="text-slate-300 text-sm leading-relaxed mb-5">
          Deliver end-to-end hiring intelligence to clients without building the infrastructure yourself. Atlas gives your operation the role definition, candidate matching, and governance layer you need to scale mandates without scaling headcount.
        </p>
        <div className="space-y-3 flex-1">
          {["Manage role intelligence and candidate positioning from a single orchestrated platform",
            "Deliver audit-ready hiring workflows and compliance documentation to every client",
            "Close the placement loop with outcome data that sharpens every future search"].map((pt, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
              <p className="text-slate-400 text-sm leading-relaxed">{pt}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-5 border-t border-amber-800/30">
          <div className="flex items-center gap-3 text-xs text-amber-400/60 font-semibold tracking-widest uppercase">
            <div className="w-8 h-0.5 bg-amber-400/30" /> Primary User Group
          </div>
        </div>
      </div>

      {/* Enterprise Organizations */}
      <div className="jdc-bento-card rounded-2xl border border-violet-800/40 hover:border-violet-500/50 bg-slate-900 p-8 flex flex-col"
        style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(168,85,247,0.07) 0%, transparent 60%), #0f172a" }}>
        <div className="flex justify-start mb-5">
          <AccentEnterprise />
        </div>
        <h3 className="text-violet-400 font-bold text-xl tracking-tight mb-3">Enterprise Organizations</h3>
        <p className="text-slate-300 text-sm leading-relaxed mb-4">
          Unify hiring intelligence across business units, geographies, and seniority bands. Atlas enforces governance, closes the signal loop between employer and candidate, and makes every hiring decision defensible at scale.
        </p>
        <div className="mt-auto bg-violet-900/10 border border-violet-800/30 rounded-lg p-3">
          <p className="text-violet-300 text-xs font-semibold mb-1">Key Benefit</p>
          <p className="text-slate-400 text-xs leading-relaxed">Standardized hiring intelligence across every team — with closed-loop outcomes that compound over time</p>
        </div>
      </div>

      {/* Large Staffing & Talent Platforms */}
      <div className="jdc-bento-card rounded-2xl border border-cyan-800/40 hover:border-cyan-500/50 bg-slate-900 p-8 flex flex-col"
        style={{ background: "radial-gradient(ellipse at 50% 90%, rgba(34,211,238,0.07) 0%, transparent 60%), #0f172a" }}>
        <div className="flex justify-start mb-5">
          <AccentStaffing />
        </div>
        <h3 className="text-cyan-400 font-bold text-xl tracking-tight mb-3">Staffing & Talent Platforms</h3>
        <p className="text-slate-300 text-sm leading-relaxed mb-4">
          Operate both sides of the talent marketplace with unified intelligence. Atlas powers the match engine, automates the workflow, and feeds every placement outcome back into the system.
        </p>
        <div className="mt-auto flex items-center gap-6">
          <div className="text-center">
            <div className="text-cyan-400 font-bold text-2xl mb-1">Both</div>
            <div className="text-slate-500 text-xs">Sides Unified</div>
          </div>
          <div className="w-px h-10 bg-cyan-800/40" />
          <div className="text-center">
            <div className="text-cyan-400 font-bold text-2xl mb-1">Zero</div>
            <div className="text-slate-500 text-xs">Signal Gaps</div>
          </div>
        </div>
      </div>

      {/* Platform Integrators — wide */}
      <div className="jdc-bento-card md:col-span-2 rounded-2xl border border-emerald-800/35 hover:border-emerald-500/45 bg-slate-900 p-8"
        style={{ background: "radial-gradient(ellipse at 90% 50%, rgba(16,185,129,0.07) 0%, transparent 60%), #0f172a" }}>
        <div className="flex flex-col md:flex-row md:items-start md:gap-8">
          <div className="flex-shrink-0 mb-5 md:mb-0 md:pt-1">
            <AccentIntegrators />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-emerald-400 font-bold text-xl tracking-tight mb-3">Platform Integrators & Talent Tech Teams</h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-5">
              Embed Atlas intelligence into existing ATS, HRIS, and talent marketplace infrastructure. The unified API layer connects role signals, candidate data, and outcome intelligence into your current stack — without rebuilding it.
            </p>
            <div className="flex flex-wrap gap-2">
              {["ATS Integration","HRIS Compatible","API-First Architecture","White-Label Ready"].map((tag, i) => (
                <div key={i} className="px-3 py-1.5 rounded-full bg-emerald-900/20 border border-emerald-800/30">
                  <span className="text-emerald-300 text-xs font-semibold">{tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
);

const ProcessFlow = () => {
  const accents = [PfIngest, PfAlign, PfOrchestrate, PfClose];
  return (
    <section className="jdc-fade jdc-fade-4">
      <div className="text-center mb-12">
        <p className="text-xs font-bold tracking-widest uppercase text-pink-400 mb-3">Unified Intelligence Pipeline</p>
        <h2 className="text-3xl font-bold text-slate-100 tracking-tight">How HEKA Atlas Works</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {FLOW_STEPS.map((step, i) => {
          const Accent = accents[i];
          return (
            <div key={i} className="atl-process-card rounded-2xl border border-slate-700/40 bg-slate-900/60 p-7 backdrop-blur-sm overflow-hidden group"
              style={{ borderTop: `2px solid ${step.color}35` }}>
              <div className="flex justify-center mb-5 opacity-65 group-hover:opacity-100 transition-opacity duration-300">
                <Accent />
              </div>
              <div className="text-sm font-bold tracking-widest uppercase mb-2" style={{ color: step.color }}>
                {step.label}
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{step.description}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ComparisonTable = () => (
  <section className="jdc-fade jdc-fade-4">
    <div className="text-center mb-12">
      <h2 className="text-3xl font-bold text-slate-100 tracking-tight mb-4">{COMPARISON.title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      <div className="jdc-comparison-card rounded-2xl border border-red-800/30 bg-gradient-to-br from-red-900/10 to-slate-900/50 p-8 backdrop-blur-sm group">
        <h3 className="text-red-400 font-bold text-xl mb-6 flex items-center gap-2"><span className="text-2xl">❌</span> {COMPARISON.beforeTitle}</h3>
        <div className="space-y-4">
          {COMPARISON.before.map((item, i) => (
            <div key={i} className="jdc-comparison-item flex items-start gap-3">
              <Icon d={ICONS.x} size={18} className="text-red-400 flex-shrink-0 mt-0.5 jdc-x-icon" />
              <p className="text-slate-400 text-sm leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="jdc-comparison-card rounded-2xl border border-emerald-800/30 bg-gradient-to-br from-emerald-900/10 to-slate-900/50 p-8 backdrop-blur-sm group">
        <h3 className="text-emerald-400 font-bold text-xl mb-6 flex items-center gap-2"><span className="text-2xl">✅</span> {COMPARISON.afterTitle}</h3>
        <div className="space-y-4">
          {COMPARISON.after.map((item, i) => (
            <div key={i} className="jdc-comparison-item flex items-start gap-3">
              <Icon d={ICONS.check} size={18} className="text-emerald-400 flex-shrink-0 mt-0.5 jdc-check-icon" style={{ animationDelay: `${i * 0.1}s` }} />
              <p className="text-slate-300 text-sm leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const AtlasTimeline = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="jdc-fade jdc-fade-5">
      <div className="text-center mb-16">
        <p className="text-xs font-bold tracking-widest uppercase text-orange-400 mb-3">Closed-Loop System</p>
        <h2 className="text-3xl font-bold text-slate-100 tracking-tight">The Unified Hiring Intelligence Cycle</h2>
        <p className="text-slate-400 mt-4 max-w-xl mx-auto text-sm leading-relaxed">
          From role blueprint to closed placement loop — every stage orchestrated by Atlas, powered by signals from both Codex and Odyssey.
        </p>
      </div>

      {/* Desktop — 6-column */}
      <div className="hidden lg:block relative">
        <div className="absolute top-[20px] left-[3%] right-[3%] h-0.5">
          <div className="h-full w-full bg-gradient-to-r from-orange-500/20 via-violet-500/30 via-cyan-500/30 via-emerald-500/30 via-amber-500/25 to-pink-500/20 atl-glow" />
        </div>
        <div className="absolute top-[20px] left-[3%] right-[3%] h-0.5 bg-gradient-to-r from-orange-400 via-violet-400 via-cyan-400 via-emerald-400 via-amber-400 to-pink-400 origin-left atl-h-timeline-line" />
        <div className="grid grid-cols-6 gap-3 px-2">
          {TIMELINE_STEPS.map((item, i) => (
            <div key={i} className="flex flex-col items-center"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.6s ease ${0.4 + i * 0.12}s` }}>
              <div className="mb-5 w-10 h-10 rounded-full border-2 flex items-center justify-center z-10"
                style={{ borderColor: item.nodeColor, backgroundColor: `${item.nodeColor}18`, boxShadow: visible ? `0 0 14px ${item.nodeColor}55` : 'none', transition: `box-shadow 0.5s ease ${0.6 + i * 0.12}s` }}>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.nodeColor }} />
              </div>
              <div className="atl-tl-card w-full rounded-xl p-4 backdrop-blur-sm"
                style={{ border: `1px solid ${item.border}`, background: `radial-gradient(ellipse at 50% 0%, ${item.glowColor} 0%, transparent 70%), #0f172acc` }}>
                <div className="text-xs font-bold tracking-widest mb-1.5" style={{ color: item.color }}>STEP {item.step}</div>
                <h4 className="text-slate-100 font-bold text-xs mb-1 leading-tight">{item.title}</h4>
                <div className="text-xs mb-2.5 font-medium leading-tight" style={{ color: `${item.color}90` }}>{item.subtitle}</div>
                <p className="text-slate-400 text-xs leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden relative pl-8">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-400/40 via-violet-400/40 via-cyan-400/40 via-emerald-400/40 via-amber-400/40 to-pink-400/40 jdc-timeline-line" />
        <div className="space-y-5">
          {TIMELINE_STEPS.map((item, i) => (
            <div key={i} className="relative"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-16px)', transition: `all 0.5s ease ${0.3 + i * 0.12}s` }}>
              <div className="absolute -left-[22px] top-3 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                style={{ borderColor: item.nodeColor, backgroundColor: `${item.nodeColor}20` }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.nodeColor }} />
              </div>
              <div className="jdc-timeline-card rounded-xl p-5 backdrop-blur-sm"
                style={{ border: `1px solid ${item.border}`, background: `radial-gradient(ellipse at 0% 50%, ${item.glowColor} 0%, transparent 70%), #0f172acc` }}>
                <div className="text-xs font-bold tracking-widest mb-1" style={{ color: item.color }}>{item.title}</div>
                <div className="text-xs mb-2 font-medium" style={{ color: `${item.color}80` }}>{item.subtitle}</div>
                <p className="text-slate-400 text-xs leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────

const HekaAtlas = ({ setCurrentPage = () => {} }) => (
  <>
    <style>{styles}</style>
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="space-y-28 max-w-6xl mx-auto px-4 pb-20">

        {/* Hero */}
        <section className="relative pt-24 pb-16 text-center jdc-fade jdc-fade-1">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-600/40 bg-orange-900/15 mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-orange-400 text-xs font-bold tracking-widest uppercase">Orchestration Intelligence Platform</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-3">
            <span className="vp-title">Heka ATLAS</span>
          </h1>
          <p className="text-slate-300 text-2xl md:text-3xl font-bold tracking-wide mt-2 mb-6">
            Where Roles Meet the Right People.
          </p>

          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed mb-8">
            Atlas unifies role intelligence and career intelligence into one orchestrated system — where hiring decisions are data-backed, workflows are automated, and every placement closes the loop for the next hire.
          </p>
          <div className="text-sm tracking-wide text-orange-300/70">Built for Platform Leaders, Talent Operations & Hiring Teams</div>
        </section>

        <div className="jdc-divider" />
        <IntegrationBridge setCurrentPage={setCurrentPage} />
        <div className="jdc-divider" />
        <FeatureGrid />
        <div className="jdc-divider" />
        <BentoCards />
        <div className="jdc-divider" />
        <AtlasTimeline />
        <div className="jdc-divider" />
        <ComparisonTable />
        <div className="jdc-divider" />
        {/* ── CTA SECTION ─────────────────────────────────── */}
        <section className="api-fade api-fade-2 text-center p-12 rounded-2xl bg-gradient-to-br from-amber-900/20 to-violet-900/20 border border-amber-800/30 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-amber-400 mb-4">
            Ready to Unify Your Hiring Intelligence?
          </h2>
          <p className="text-violet-300 mb-8 max-w-2xl mx-auto">
            Talk to our team about integrating Atlas into your talent infrastructure — 
            from ATS connections to custom Recruitment Process Outsourcing workflows and enterprise compliance requirements.
          </p>
          
          <a href="https://wa.me/919235327048?text=Hi%2C%20I%27m%20interested%20in%20HEKA%20Atlas%20for%20unified%20hiring%20intelligence.%20I%27d%20like%20to%20discuss%20integration%20options." 
            target="_blank" rel="noopener noreferrer"
            className="api-btn inline-flex items-center gap-2 bg-amber-600 text-black px-8 py-3 rounded-lg font-bold no-underline text-sm">
            <Icon d={ICONS.whatsapp} size={16} />
            Chat With Our Team
          </a>
        </section>
      </div>
    </div>
  </>
);

export default HekaAtlas;